import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../lib/api';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { SectionCard, slugify } from './SectionCard';
import ImageUpload from '../ImageUpload';
import {
  Plus, Trash2, Edit, Save, X, Loader2, Image as ImageIcon, Eye, EyeOff, Images,
} from 'lucide-react';

export default function ProductsManager({ onSaved, toast }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showGalleryPicker, setShowGalleryPicker] = useState(false);
  const [galleryItems, setGalleryItems] = useState([]);
  const [addToGallery, setAddToGallery] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/products');
      setItems(data);
    } catch (e) {
      toast({ title: 'Failed to load', variant: 'destructive' });
    } finally { setLoading(false); }
  }, [toast]);

  const loadGallery = useCallback(async () => {
    try { const { data } = await api.get('/admin/gallery'); setGalleryItems(data); } catch { /* ignore */ }
  }, []);

  useEffect(() => { load(); loadGallery(); }, [load, loadGallery]);

  const startNew = () => { setEditing({ slug: '', name: '', image: '', description: '', sort_order: items.length, active: true, seo_title: '', seo_description: '', seo_keywords: '' }); setAddToGallery(true); setShowForm(true); };
  const startEdit = (it) => { setEditing({ ...it }); setAddToGallery(false); setShowForm(true); };
  const cancel = () => { setEditing(null); setShowForm(false); setShowGalleryPicker(false); };

  const save = async () => {
    if (!editing.name || !editing.image) {
      toast({ title: 'Name and Image are required', variant: 'destructive' });
      return;
    }
    if (!editing.slug) editing.slug = slugify(editing.name);
    try {
      if (editing.id) {
        await api.put(`/admin/products/${editing.id}`, editing);
        toast({ title: 'Product updated' });
      } else {
        await api.post('/admin/products', editing);
        toast({ title: 'Product added' });
      }
      // Optionally push the product image to the Gallery (skip if duplicate)
      if (addToGallery && editing.image) {
        const exists = galleryItems.some((g) => g.image === editing.image);
        if (!exists) {
          try {
            await api.post('/admin/gallery', { image: editing.image, caption: editing.name, sort_order: galleryItems.length });
            toast({ title: 'Also added to Gallery' });
          } catch { /* ignore gallery error */ }
        }
      }
      cancel();
      load();
      loadGallery();
      onSaved && onSaved();
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail || 'Try again', variant: 'destructive' });
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await api.delete(`/admin/products/${id}`);
      toast({ title: 'Product deleted' });
      load();
      onSaved && onSaved();
    } catch (err) {
      toast({ title: 'Failed', variant: 'destructive' });
    }
  };

  return (
    <SectionCard
      title="Products"
      subtitle={`${items.length} item${items.length !== 1 ? 's' : ''}`}
      action={<button data-testid="add-product-btn" onClick={startNew} className="btn-brand text-sm"><Plus className="w-4 h-4" /> Add Product</button>}
    >
      {showForm && (
        <div className="mb-4 p-4 border border-[#f7941e]/30 bg-[#fff7e6] rounded-lg space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label>Name *</Label>
              <Input data-testid="product-name-input" value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value, slug: editing.slug || slugify(e.target.value) })} />
            </div>
            <div>
              <Label>Slug (URL-friendly)</Label>
              <Input value={editing.slug} onChange={e => setEditing({ ...editing, slug: slugify(e.target.value) })} placeholder="auto-generated" />
            </div>
          </div>
          <div>
            <ImageUpload label="Product Image *" value={editing.image} onChange={(v) => setEditing({ ...editing, image: v })} placeholder="Paste image URL or upload a file" />
            <div className="flex flex-wrap items-center gap-3 mt-2">
              <button
                type="button"
                onClick={() => setShowGalleryPicker((o) => !o)}
                className="inline-flex items-center gap-1.5 text-xs bg-white border border-[#f7941e]/40 text-[#e67e00] hover:bg-[#fff7e6] px-3 py-1.5 rounded"
                data-testid="pick-from-gallery-btn"
              >
                <Images className="w-3.5 h-3.5" /> {showGalleryPicker ? 'Hide gallery' : 'Pick from Gallery'}
              </button>
              <label className="inline-flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
                <input type="checkbox" checked={addToGallery} onChange={(e) => setAddToGallery(e.target.checked)} />
                Also add this image to Gallery
              </label>
            </div>
            {showGalleryPicker && (
              <div className="mt-2 border border-gray-200 rounded p-2 bg-white max-h-56 overflow-y-auto">
                {galleryItems.length === 0 ? (
                  <p className="text-xs text-gray-500 p-2">Gallery is empty. Add images in the Gallery tab first.</p>
                ) : (
                  <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
                    {galleryItems.map((g) => (
                      <button
                        type="button"
                        key={g.id}
                        onClick={() => { setEditing({ ...editing, image: g.image }); setShowGalleryPicker(false); toast({ title: 'Image selected from gallery' }); }}
                        className={`aspect-square border rounded overflow-hidden hover:ring-2 hover:ring-[#f7941e] transition ${editing.image === g.image ? 'ring-2 ring-[#f7941e]' : 'border-gray-200'}`}
                        title={g.caption || ''}
                      >
                        <img src={g.image} alt={g.caption || ''} className="w-full h-full object-cover" loading="lazy" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <div>
            <Label>Description</Label>
            <Textarea rows={3} value={editing.description || ''} onChange={e => setEditing({ ...editing, description: e.target.value })} />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label>Sort Order</Label>
              <Input type="number" value={editing.sort_order || 0} onChange={e => setEditing({ ...editing, sort_order: parseInt(e.target.value) || 0 })} />
            </div>
            <div className="flex items-end">
              <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={editing.active} onChange={e => setEditing({ ...editing, active: e.target.checked })} />
                Active (visible on website)
              </label>
            </div>
          </div>

          <div className="pt-3 mt-1 border-t border-[#f7941e]/30">
            <p className="font-display text-xs uppercase tracking-[3px] text-[#f7941e] mb-1">SEO (Search Engine Optimization)</p>
            <p className="text-[11px] text-gray-500 mb-3">Leave blank to auto-use product name + description.</p>
            <div className="space-y-3">
              <div>
                <Label>Meta Title <span className="text-gray-400 text-xs">(60 chars ideal)</span></Label>
                <Input data-testid="product-seo-title" value={editing.seo_title || ''} onChange={e => setEditing({ ...editing, seo_title: e.target.value })} placeholder={`e.g. ${editing.name || 'Product Name'} | Ess Bee Heaters`} />
              </div>
              <div>
                <Label>Meta Description <span className="text-gray-400 text-xs">(160 chars ideal)</span></Label>
                <Textarea data-testid="product-seo-description" rows={2} value={editing.seo_description || ''} onChange={e => setEditing({ ...editing, seo_description: e.target.value })} placeholder="Short description shown on Google search results" />
              </div>
              <div>
                <Label>Meta Keywords <span className="text-gray-400 text-xs">(comma separated)</span></Label>
                <Input data-testid="product-seo-keywords" value={editing.seo_keywords || ''} onChange={e => setEditing({ ...editing, seo_keywords: e.target.value })} placeholder="cartridge heater, industrial heater, Delhi" />
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <button data-testid="save-product-btn" onClick={save} className="btn-brand text-sm"><Save className="w-4 h-4" /> Save</button>
            <button onClick={cancel} className="inline-flex items-center gap-1.5 text-sm bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded"><X className="w-4 h-4" /> Cancel</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#f7941e]" /></div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map(it => (
            <div key={it.id} data-testid={`product-card-${it.slug}`} className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="aspect-[4/3] bg-[#fff7e6] flex items-center justify-center">
                {it.image ? <img src={it.image} alt={it.name} className="max-h-full max-w-full object-contain" /> : <ImageIcon className="w-8 h-8 text-gray-300" />}
              </div>
              <div className="p-3">
                <p className="font-display text-sm uppercase truncate" title={it.name}>{it.name}</p>
                <p className="text-[11px] text-gray-500 truncate">{it.slug}</p>
                <div className="flex items-center justify-between mt-2">
                  <span className={`text-[10px] uppercase px-2 py-0.5 rounded-full ${it.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                    {it.active ? <><Eye className="w-3 h-3 inline" /> Live</> : <><EyeOff className="w-3 h-3 inline" /> Hidden</>}
                  </span>
                  <div className="flex gap-1">
                    <button onClick={() => startEdit(it)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"><Edit className="w-4 h-4" /></button>
                    <button onClick={() => remove(it.id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
}
